import * as SSR from "../../api/hooks/lib/ssr.js"
import type { ComponentType } from "svelte"

import Cryptcha from "cryptcha"

import { cryptchaSiteId } from "../../api/hooks/config-client.js"
import { writable } from "svelte/store"

export const formatPrice = SSR.formatPrice

export class LRUCache<T> {
    private values: Map<string, T> = new Map<string, T>()
    private maxEntries: number = 100

    public get(key: string): T {
        const hasKey = this.values.has(key)
        let entry: T
        if (hasKey) {
            // peek the entry, re-insert for LRU strategy
            entry = this.values.get(key)
            this.values.delete(key)
            this.values.set(key, entry)
        }

        return entry
    }

    public put(key: string, value: T) {
        if (this.values.size >= this.maxEntries) {
            // least-recently used cache eviction strategy
            const keyToDelete = this.values.keys().next().value

            this.values.delete(keyToDelete)
        }

        this.values.set(key, value)
    }
}

export const debounce = (callback: Function, wait: number) => {
    let timeout = null
    return (...args) => {
        const next = () => callback(...args)
        clearTimeout(timeout)
        timeout = setTimeout(next, wait)
    }
}

export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

export function equal(a, b) {
    return JSON.stringify(a) == JSON.stringify(b)
}

export function firstProductImage(product: Product, filter: undefined | "xs" | "s" | "m" | "l" | "xl" = undefined) {
    return (
        (product.images?.length && product.images[0].file?.src + (filter ? "?filter=" + filter : "")) ||
        "/media/kein-bild.jpg"
    )
}

export function clickOutside(node: Node, callback: (arg0: Node) => void) {
    // @ts-ignore
    const handleOutsideClick = ({ target }) => {
        if (!node.contains(target)) {
            callback(target)
        }
    }

    typeof window !== "undefined" && window.addEventListener("click", handleOutsideClick, true)

    return {
        destroy() {
            typeof window !== "undefined" && window.removeEventListener("click", handleOutsideClick, true)
        },
    }
}

export function filesize(s: number): string {
    let ex = "Byte"
    if (s > 1024) {
        s /= 1024
        ex = "KB"
    }
    if (s > 1024) {
        s /= 1024
        ex = "MB"
    }
    if (s > 1024) {
        s /= 1024
        ex = "GB"
    }

    return Math.round(s) + " " + ex
}

export function convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

export function getParameterByName(name: string, url = typeof window !== "undefined" && window.location?.href) {
    name = name.replace(/[\[\]]/g, "\\$&")
    var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
        results = regex.exec(url)
    if (!results) return null
    if (!results[2]) return ""
    return decodeURIComponent(results[2].replace(/\+/g, " "))
}

export const obj2str = SSR.obj2str
export const getProductOption = SSR.getProductOption
export const getProductPrice = SSR.getProductPrice

export function isSelectableOption(optionIdent: string, selectedOptions: string[], product: Product): boolean {
    if (optionIdent == "installation_box_left") {
        // via separate product
        return false
    }
    if (optionIdent.includes("#")) {
        const parts = optionIdent.split("#")
        const baseMatch = parts[0].match(/^(\-)?(.*)$/)
        const baseNegate = !!baseMatch[1]
        const baseOpt = baseMatch[2]
        const productOptions = product?.options?.map((o) => o.option)
        // @ts-ignore
        if (productOptions && productOptions.indexOf(baseOpt) === -1) {
            // zb. rsa2 Namensschild für Plan Serie, bei der RSA2 keine Option sondern inkl. ist
            return true
        }
        const baseSelected = !!(selectedOptions && selectedOptions.indexOf(baseOpt) != -1)
        if (baseNegate && baseSelected) {
            // - und in Auswahl -> nicht erlaubt
            return false
        }
        if (!baseNegate && !baseSelected) {
            // kein - und nicht in Auswahl -> nicht erlaubt
            return false
        }
    }
    return true
}

export function tooltip(node: Node, data?: Tooltip) {
    if (data) {
        const when = data.when || "hover"
        const span = document.createElement("div")

        const bubbleding = document.createElement("div")
        bubbleding.className = "bubbleding"
        span.appendChild(bubbleding)

        const content = document.createElement("div")
        content.className = "content"
        span.appendChild(content)

        const baseClass = "tooltip active " + (data.className || "")
        span.className = baseClass
        if (data.style) span.style.cssText = data.style

        let rendered = false
        const doOpen = (e: MouseEvent) => {
            if (typeof data.content == "string") {
                content.innerText = data.content
            } else if (typeof data.content == "object" && !rendered) {
                rendered = true
                const component = data.content.component as ComponentType
                new component({
                    target: content,
                    props: {
                        // send doClose callback to component for closing via custom button
                        doClose,
                        ...data.content.props,
                    },
                })
            }

            const fullWith = window.innerWidth // document.body.clientWidth
            const fullHeight = window.innerHeight // document.body.clientHeight
            const mouseX = e.clientX // e.pageX
            const mouseY = e.clientY // e.pageY

            let leftOffset = 0
            if (mouseX < 120) {
                leftOffset = 120 - mouseX
            }
            if (e.pageX > window.innerWidth - 120) {
                leftOffset = -120 + (fullWith - mouseX)
            }
            bubbleding.style.left = leftOffset ? 100 - leftOffset + "px" : "50%"
            span.style.left = mouseX + leftOffset + "px"

            if (data.className?.includes("bottom")) {
                span.style.bottom = "auto"
                span.style.top = mouseY + 20 + "px"
            } else {
                span.style.bottom = fullHeight - mouseY + 20 + "px"
            }

            if (!span.parentNode) {
                document.body.appendChild(span)
            }
        }
        const doClose = () => {
            span.remove()
        }

        const clickOutside = ({ target }) => {
            if (!span.contains(target)) {
                doClose()
            }
        }

        if (typeof window !== "undefined") {
            if (when == "click") {
                node.addEventListener("click", doOpen, true)
                window.addEventListener("click", clickOutside, true)
            } else {
                node.addEventListener("mouseover", doOpen, true)
                node.addEventListener("mousemove", doOpen, true)
                node.addEventListener("mouseout", doClose, true)
            }
        }

        return {
            destroy() {
                span.remove()
                if (typeof window !== "undefined") {
                    if (when == "hover") {
                        node.removeEventListener("mouseover", doOpen, true)
                        node.removeEventListener("mousemove", doOpen, true)
                        node.removeEventListener("mouseout", doClose, true)
                    } else {
                        window.removeEventListener("click", clickOutside, true)
                    }
                }
            },
        }
    }
}

export const cryptchaSolution = writable("")
export const cryptchaSolutionId = writable("")
let cryptcha
let cryptchaTarget
export async function initCryptcha(target: HTMLElement) {
    if (!cryptcha) {
        // create new instance
        cryptchaTarget = document.createElement("div")
        cryptcha = new Cryptcha({
            siteId: cryptchaSiteId,
            target: cryptchaTarget,
            baseUrl: "https://cryptcha.webmakers.de/api/command",
            log: false,
            autoSolve: true,
            onSolved({ solution, solutionId }) {
                cryptchaSolution.set(solution)
                cryptchaSolutionId.set(solutionId)
            },
        })
    }

    if (target) {
        target.innerHTML = ""
        target.appendChild(cryptchaTarget)
    }
}

export function resetCryptcha() {
    cryptcha = null
    cryptchaSolution.set("")
    cryptchaSolutionId.set("")
}
